import React from 'react';
import styled from 'styled-components';
import ComponentDemo from '../../components/ComponentDemo';
import CodeBlock from '../../components/CodeBlock';

const PageHeader = styled.div`
  margin-bottom: 48px;
`;

const Title = styled.h1`
  font-size: 36px;
  font-weight: 700;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 12px 0;
`;

const Subtitle = styled.p`
  font-size: 18px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  margin: 0;
  line-height: 1.6;
`;

const Section = styled.section`
  margin-bottom: 64px;
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 8px 0;
`;

const SectionDescription = styled.p`
  font-size: 15px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  line-height: 1.7;
  margin: 0 0 32px 0;
`;

const ShadowCard = styled.div`
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255,255,255,0.05)' : '#ffffff'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.1)' : '#E2E8F0'};
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
`;

const ShadowLabel = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const ShadowName = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${(props) => (props.theme.name === 'dark' ? '#E2E8F0' : '#0F172A')};
`;

const ShadowValue = styled.code`
  font-size: 11px;
  font-family: 'SF Mono', Monaco, Consolas, 'Courier New', monospace;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.05)'};
  padding: 6px 10px;
  border-radius: 4px;
  max-width: 400px;
  overflow-x: auto;
  display: block;
`;

const ShadowBox = styled.div`
  width: 200px;
  height: 120px;
  background: ${(props) =>
    props.theme.name === 'dark' ? '#1E293B' : '#ffffff'};
  border-radius: 8px;
  box-shadow: ${(props) => props.shadow};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => (props.theme.name === 'dark' ? '#E2E8F0' : '#64748B')};
  font-size: 14px;
  font-weight: 600;
  margin: 0 auto;
`;

const ShadowUsage = styled.div`
  font-size: 13px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  margin-top: 16px;
  line-height: 1.5;
`;

const ExampleCard = styled.div`
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255,255,255,0.05)' : '#ffffff'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.1)' : '#E2E8F0'};
  border-radius: 8px;
  padding: 20px;
  box-shadow: ${(props) => props.shadow};
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${(props) => props.hoverShadow || props.shadow};
  }
`;

const ExampleTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 8px 0;
`;

const ExampleText = styled.p`
  font-size: 14px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  line-height: 1.6;
  margin: 0;
`;

const Shadows = () => {
  return (
    <div>
      <PageHeader>
        <Title>Shadows</Title>
        <Subtitle>
          Shadows create depth and hierarchy in the interface, helping users
          understand layering and focus. Our shadow system adapts to light and
          dark themes for optimal visibility.
        </Subtitle>
      </PageHeader>

      <Section>
        <SectionTitle>Shadow Scale</SectionTitle>
        <SectionDescription>
          Four elevation levels plus a special glow effect for dark mode
          highlights.
        </SectionDescription>

        <ShadowCard>
          <ShadowLabel>
            <ShadowName>Small (sm)</ShadowName>
          </ShadowLabel>
          <ShadowValue>
            Light: 0 1px 3px rgba(0, 0, 0, 0.12)
            <br />
            Dark: 0 1px 3px rgba(0, 0, 0, 0.3)
          </ShadowValue>
          <ComponentDemo compact>
            <ShadowBox
              shadow="0 1px 3px rgba(0, 0, 0, 0.3)"
            >
              Small Shadow
            </ShadowBox>
          </ComponentDemo>
          <ShadowUsage>
            Use for: Subtle elevation, small cards, buttons
          </ShadowUsage>
        </ShadowCard>

        <ShadowCard>
          <ShadowLabel>
            <ShadowName>Medium (md)</ShadowName>
          </ShadowLabel>
          <ShadowValue>
            Light: 0 2px 8px rgba(0, 0, 0, 0.08)
            <br />
            Dark: 0 2px 8px rgba(0, 0, 0, 0.4)
          </ShadowValue>
          <ComponentDemo compact>
            <ShadowBox shadow="0 2px 8px rgba(0, 0, 0, 0.4)">
              Medium Shadow
            </ShadowBox>
          </ComponentDemo>
          <ShadowUsage>
            Use for: Standard cards, dropdowns, tooltips
          </ShadowUsage>
        </ShadowCard>

        <ShadowCard>
          <ShadowLabel>
            <ShadowName>Large (lg)</ShadowName>
          </ShadowLabel>
          <ShadowValue>
            Light: 0 4px 16px rgba(0, 0, 0, 0.1)
            <br />
            Dark: 0 4px 16px rgba(0, 0, 0, 0.5)
          </ShadowValue>
          <ComponentDemo compact>
            <ShadowBox shadow="0 4px 16px rgba(0, 0, 0, 0.5)">
              Large Shadow
            </ShadowBox>
          </ComponentDemo>
          <ShadowUsage>
            Use for: Modals, popovers, floating panels
          </ShadowUsage>
        </ShadowCard>

        <ShadowCard>
          <ShadowLabel>
            <ShadowName>Extra Large (xl)</ShadowName>
          </ShadowLabel>
          <ShadowValue>
            Light: 0 8px 32px rgba(0, 0, 0, 0.12)
            <br />
            Dark: 0 8px 32px rgba(0, 0, 0, 0.6)
          </ShadowValue>
          <ComponentDemo compact>
            <ShadowBox shadow="0 8px 32px rgba(0, 0, 0, 0.6)">
              Extra Large Shadow
            </ShadowBox>
          </ComponentDemo>
          <ShadowUsage>
            Use for: Major modals, dialogs, important overlays
          </ShadowUsage>
        </ShadowCard>

        <ShadowCard>
          <ShadowLabel>
            <ShadowName>Glow (Dark Theme Only)</ShadowName>
          </ShadowLabel>
          <ShadowValue>0 0 20px rgba(250, 70, 22, 0.2)</ShadowValue>
          <ComponentDemo compact>
            <ShadowBox shadow="0 0 20px rgba(250, 70, 22, 0.3)">
              Glow Effect
            </ShadowBox>
          </ComponentDemo>
          <ShadowUsage>
            Use for: Highlighting primary actions, focus states in dark mode
          </ShadowUsage>
        </ShadowCard>

        <CodeBlock>
{`// Access shadows from theme
const Card = styled.div\`
  box-shadow: \${props => props.theme.shadows.md};

  &:hover {
    box-shadow: \${props => props.theme.shadows.lg};
  }
\`;

// Glow effect for dark theme
const PrimaryButton = styled.button\`
  box-shadow: \${props =>
    props.theme.name === 'dark'
      ? props.theme.shadows.glow
      : props.theme.shadows.sm
  };
\`;`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Interactive Examples</SectionTitle>
        <SectionDescription>
          Hover over these cards to see shadow transitions in action.
        </SectionDescription>

        <ComponentDemo
          label="sm → md transition"
          description="Hover to see elevation change"
          gap="32px"
          wrap
        >
          <ExampleCard
            shadow="0 1px 3px rgba(0, 0, 0, 0.12)"
            hoverShadow="0 2px 8px rgba(0, 0, 0, 0.15)"
          >
            <ExampleTitle>Small to Medium</ExampleTitle>
            <ExampleText>
              Subtle elevation change for interactive elements
            </ExampleText>
          </ExampleCard>
        </ComponentDemo>

        <ComponentDemo
          label="md → lg transition"
          description="Hover to see elevation change"
          gap="32px"
          wrap
        >
          <ExampleCard
            shadow="0 2px 8px rgba(0, 0, 0, 0.08)"
            hoverShadow="0 4px 16px rgba(0, 0, 0, 0.12)"
          >
            <ExampleTitle>Medium to Large</ExampleTitle>
            <ExampleText>
              More pronounced lift for cards and panels
            </ExampleText>
          </ExampleCard>
        </ComponentDemo>

        <ComponentDemo
          label="lg → xl transition"
          description="Hover to see elevation change"
          gap="32px"
          wrap
        >
          <ExampleCard
            shadow="0 4px 16px rgba(0, 0, 0, 0.1)"
            hoverShadow="0 8px 32px rgba(0, 0, 0, 0.15)"
          >
            <ExampleTitle>Large to Extra Large</ExampleTitle>
            <ExampleText>
              Dramatic lift for important interactive elements
            </ExampleText>
          </ExampleCard>
        </ComponentDemo>
      </Section>

      <Section>
        <SectionTitle>Card Shadow System</SectionTitle>
        <SectionDescription>
          Cards use a specific shadow system defined in the theme for
          consistency across light and dark modes.
        </SectionDescription>

        <CodeBlock>
{`// Card-specific shadows from theme
const Card = styled.div\`
  background: \${props => props.theme.colors.card.background};
  border: 1px solid \${props => props.theme.colors.card.border};
  box-shadow: \${props => props.theme.colors.card.shadow};
  border-radius: 8px;
  transition: all 0.2s;

  &:hover {
    box-shadow: \${props => props.theme.colors.card.shadowHover};
  }
\`;

// Theme values:
// Light theme:
// - shadow: rgba(0, 0, 0, 0.1) 0px 1px 3px
// - shadowHover: rgba(0, 0, 0, 0.15) 0px 4px 12px

// Dark theme:
// - shadow: rgba(0, 0, 0, 0.2) 0px 1px 3px
// - shadowHover: rgba(0, 0, 0, 0.4) 0px 4px 12px`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Best Practices</SectionTitle>
        <SectionDescription>
          Guidelines for using shadows effectively in your components.
        </SectionDescription>

        <ExampleCard shadow="0 2px 8px rgba(0, 0, 0, 0.08)">
          {/* eslint-disable-next-line jsx-a11y/accessible-emoji */}
          <ExampleTitle>✅ Do</ExampleTitle>
          <ExampleText>
            • Use shadows to indicate elevation and layering
            <br />
            • Increase shadow on hover to show interactivity
            <br />
            • Use lighter shadows in light theme, darker in dark theme
            <br />
            • Keep shadow transitions smooth (0.2s - 0.3s)
            <br />• Use glow effect sparingly for primary CTAs in dark mode
          </ExampleText>
        </ExampleCard>

        <ExampleCard
          shadow="0 2px 8px rgba(0, 0, 0, 0.08)"
          style={{ marginTop: '24px' }}
        >
          {/* eslint-disable-next-line jsx-a11y/accessible-emoji */}
          <ExampleTitle>❌ Don't</ExampleTitle>
          <ExampleText>
            • Don't use excessive shadows that overwhelm the interface
            <br />
            • Don't mix shadow systems - use theme values consistently
            <br />
            • Don't apply large shadows to small elements
            <br />
            • Don't forget to test shadows in both light and dark themes
            <br />• Don't use shadows as the only indicator of interactivity
          </ExampleText>
        </ExampleCard>
      </Section>

      <Section>
        <SectionTitle>Common Patterns</SectionTitle>
        <CodeBlock>
{`// Dropdown with shadow
const Dropdown = styled.div\`
  position: absolute;
  background: \${props =>
    props.theme.name === 'dark' ? '#1E293B' : '#ffffff'
  };
  border: 1px solid \${props => props.theme.colors.border};
  box-shadow: \${props => props.theme.shadows.lg};
  border-radius: 8px;
  padding: 8px;
\`;

// Modal with maximum elevation
const Modal = styled.div\`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: \${props =>
    props.theme.name === 'dark' ? '#1E293B' : '#ffffff'
  };
  border-radius: 12px;
  box-shadow: \${props => props.theme.shadows.xl};
  padding: 32px;
  z-index: 1000;
\`;

// Button with subtle shadow
const Button = styled.button\`
  background: \${props => props.theme.colors.primary};
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  box-shadow: \${props => props.theme.shadows.sm};
  transition: all 0.2s;

  &:hover {
    box-shadow: \${props => props.theme.shadows.md};
    transform: translateY(-1px);
  }

  &:active {
    box-shadow: \${props => props.theme.shadows.sm};
    transform: translateY(0);
  }
\`;`}
        </CodeBlock>
      </Section>
    </div>
  );
};

export default Shadows;